import React from "react";
import PropTypes from "prop-types";
import selectOptions from "shared/prop_types/selectOptions";
import marketingDataCategories from "shared/prop_types/marketingDataCategories";
import { Card, CardBody, CardHeader, CardTitle, Col, FormFeedback, FormGroup, Label, Row } from "reactstrap";
import { Field, ErrorMessage } from "formik";

import TagListInput from "shared/react_components/TagListInput";
import AddList from "shared/react_components/AddList";
import { Select } from "shared/formik/formik_helpers";
import { findOption } from "shared/utils/select_helpers";
import { SlotContent } from "shared/react_components/slots";
import errorsForField from "shared/formik/errorsForField";
import MaterialIcon from "shared/react_components/MaterialIcon";
import UserLicenseSettings from "./UserLicenseSettings";

const availableRolesGivenLicenses = (userLicenses, availableUserLicenses) => (
  (userLicenses.salesUserLicense && !userLicenses.marketingUserLicense)
    ? availableUserLicenses.find(({ name }) => name === "salesUserLicense").roles
    : availableUserLicenses.find(({ name }) => name === "marketingUserLicense").roles
);

const currentRoleAvailable = (currentRole, availableRoles) => {
  const roleValues = availableRoles.map(({ value }) => value);
  return roleValues.includes(currentRole);
};

const onLicensesChange = (availableUserLicenses) => (form, fieldName, checked) => {
  const { values: { userLicenses, role } } = form;
  const licenseName = fieldName.replace("userLicenses.", "");
  const updatedUserLicenseSelections = {
    ...userLicenses,
    [licenseName]: checked
  };

  const roles = availableRolesGivenLicenses(updatedUserLicenseSelections, availableUserLicenses);
  !currentRoleAvailable(role, roles) && form.setFieldValue("role", roles[0].value);
};

const UserPermissions = ({
  roles,
  rolePermissionsUrl,
  marketingDataDictionaryUrl,
  marketingDataCategories,
  updateReportFilters,
  manageUserLicenses,
  availableUserLicenses,
  role
}) => (
  <Card>
    <CardHeader tag="h3">Permissions</CardHeader>
    <CardBody>
      {manageUserLicenses && (
        <Row>
          <Col md={9}>
            <UserLicenseSettings
              availableUserLicenses={availableUserLicenses}
              onLicensesChange={onLicensesChange(availableUserLicenses)}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col md={9}>
          <FormGroup name="roleGroup" className="sb-field-group">
            <CardTitle>Role</CardTitle>
            <Field name="role">
              {({ form, field }) => {
                const { values: { userLicenses } } = form;
                const availableRoles = (manageUserLicenses && userLicenses)
                  ? availableRolesGivenLicenses(userLicenses, availableUserLicenses)
                  : roles;

                return <Select form={form} field={field} options={availableRoles}/>;
              }}
            </Field>
            <ErrorMessage name="role">
              {msg => <FormFeedback className="d-block">{msg}</FormFeedback>}
            </ErrorMessage>
            <a
              data-mount="role-permissions"
              href={rolePermissionsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about roles
            </a>
          </FormGroup>
        </Col>
      </Row>
      {updateReportFilters && role === "Filtered" && (marketingDataCategories.length
        ? <FormGroup className="sb-field-group">
          <AddList.ItemLayout>
            <SlotContent name={"item"}>
              <Row data-selector="headers">
                <Col xs={6}>
                  <Label className="sb-field-label">Marketing Data Category</Label>
                </Col>
                <Col xs={6}>
                  <Label className="sb-field-label">Marketing Data Values</Label>
                </Col>
              </Row>
            </SlotContent>
          </AddList.ItemLayout>
          <Field name="reportFilters">
            {({ field, form }) => {
              const currentFilterKeys = field.value.map(value => value.filterKey);
              const selectCategoryOptions = marketingDataCategories.filter(
                category => !currentFilterKeys.includes(category.value)
              );
              const selectValuesByCategory = marketingDataCategories.reduce(
                (obj, { value, fieldValues }) => ({
                  ...obj,
                  [value]: fieldValues.map(filter => ({ value: filter, label: filter }))
                }),
                {}
              );
              return (
                <AddList
                  items={field.value}
                  emptyItem={{ filterKey: "", filterValues: [] }}
                  onChange={value => form.setFieldValue(field.name, value)}
                  addButtonVisible={marketingDataCategories.length > currentFilterKeys.length}
                >
                  {({ item, fieldUpdater, index }) => (
                    <Row>
                      <Col xs={6}>
                        <Field
                          component={Select}
                          name="filterKey"
                          value={findOption(marketingDataCategories, item.filterKey)}
                          options={selectCategoryOptions}
                          onChange={option => fieldUpdater("filterKey")(option.value)}
                        />
                        <FormFeedback className="d-block">
                          {errorsForField(form, `reportFilters[${index}].filterKey`, {
                            requireTouched: false
                          })}
                        </FormFeedback>
                      </Col>
                      <Col xs={6}>
                        <TagListInput
                          name="filterValues"
                          isDisabled={!item.filterKey}
                          value={item.filterValues ? item.filterValues : []}
                          valueDelimiters={["Enter", "Tab", ","]}
                          options={selectValuesByCategory[item.filterKey]}
                          onChange={fieldUpdater.fromEventTarget("filterValues")}
                          isUnique={(val, vals) =>
                            !vals.find(existing => existing.toLowerCase() === val.toLowerCase())
                          }
                        />
                        <FormFeedback className="d-block">
                          {errorsForField(form, `reportFilters[${index}].filterValues`, {
                            requireTouched: false
                          })}
                        </FormFeedback>
                      </Col>
                    </Row>
                  )}
                </AddList>
              );
            }}
          </Field>
        </FormGroup>
        : <div id="categoryMessage" className="text-danger">
          <MaterialIcon name="warning"/>
          Filtered Reporting users leverage Marketing Data Categories for visibility access, please{' '}
          <a
            data-mount="market-data-dict-permissions"
            href={marketingDataDictionaryUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            create a Marketing Data Category.
          </a>
        </div>
      )}
    </CardBody>
  </Card>
);

UserPermissions.propTypes = {
  roles: selectOptions.isRequired,
  rolePermissionsUrl: PropTypes.string.isRequired,
  marketingDataDictionaryUrl: PropTypes.string,
  marketingDataCategories: marketingDataCategories,
  updateReportFilters: PropTypes.bool.isRequired,
  manageUserLicenses: PropTypes.bool,
  availableUserLicenses: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      roles: selectOptions.isRequired
    })
  ),
  role: PropTypes.string.isRequired,
  onLicensesChange: PropTypes.func
};

export default UserPermissions;
